/**
 * Paramètres organisation — onglets Identité | Localisation | Accès | Équipements | Tarification | Médias.
 */

import { useEffect, useId, useState, type ReactNode } from 'react'
import { useFilterOptions } from '../../list-search/hooks/useFilterOptions'
import { useOrganization, useUpdateOrganization } from '../hooks/useOrganization'
import { HttpError } from '../services/organizationApi'
import { MapPicker, type MapPickerBoundaryGeometry, type MapPickerPoint } from './MapPicker'
import { OrganizationSiteAssetsEditor } from './OrganizationSiteAssetsEditor'
import { OrganizationPricingProfilesSection } from './OrganizationPricingProfilesSection'
import { PrimaryGalleryEditor } from './PrimaryGalleryEditor'
import { OrganizationSitePlansEditor } from './OrganizationSitePlansEditor'
import './OrganizationSettings.css'
import { useTranslation } from 'react-i18next'
import i18n from '../../../i18n'

export type OrgSettingsTab =
  | 'identity'
  | 'location'
  | 'access'
  | 'facilities'
  | 'pricing'
  | 'media'

const TABS: { id: OrgSettingsTab; label: string; hash: string }[] = [
  { id: 'identity', get label() { return i18n.t('organizations:OrganizationSettings.identite') }, hash: 'identite' },
  { id: 'location', get label() { return i18n.t('organizations:OrganizationSettings.localisation') }, hash: 'localisation' },
  { id: 'access', get label() { return i18n.t('organizations:OrganizationSettings.acces_services') }, hash: 'acces' },
  { id: 'facilities', get label() { return i18n.t('organizations:OrganizationSettings.equipements') }, hash: 'equipements' },
  { id: 'pricing', get label() { return i18n.t('organizations:OrganizationSettings.tarification') }, hash: 'tarification' },
  { id: 'media', get label() { return i18n.t('organizations:OrganizationSettings.medias') }, hash: 'medias' },
]

function tabFromHash(hash: string): OrgSettingsTab {
  const cleaned = hash.replace(/^#/, '').toLowerCase()
  const found = TABS.find((t) => t.hash === cleaned || t.id === cleaned)
  return found?.id ?? 'identity'
}

interface OrganizationSettingsProps {
  organizationId: number
  /** Onglets à cacher (ex. le site de gestion montre les équipements dans leur propre section). */
  hiddenTabs?: OrgSettingsTab[]
  /** Cacher l'inventaire prêt-à-camper (résumé importé de CampingQuébec) : le site de gestion le remplace par les lieux. */
  hideLodgingUnits?: boolean
  /** Contenu ajouté sous l'onglet Localisation, hors du formulaire (ex. l'éditeur de périmètre du site de gestion). */
  locationExtra?: ReactNode
  /**
   * Remplace la carte du point d'accueil (MapPicker) par une autre carte qui gère aussi ce point
   * (ex. l'éditeur de périmètre du site de gestion : une seule carte pour l'accueil et les zones).
   */
  renderLocationMap?: (map: { point: MapPickerPoint | null; onPointChange: (p: MapPickerPoint) => void; disabled: boolean }) => ReactNode
}

export function OrganizationSettings({
  organizationId,
  hiddenTabs = [],
  hideLodgingUnits = false,
  locationExtra,
  renderLocationMap,
}: OrganizationSettingsProps) {
  const { t: tr } = useTranslation('organizations')
  const { data: organization, isLoading, error } = useOrganization(organizationId)
  const updateMutation = useUpdateOrganization(organizationId)
  const tabsId = useId()
  const visibleTabs = TABS.filter((tab) => !hiddenTabs.includes(tab.id))

  const [activeTab, setActiveTab] = useState<OrgSettingsTab>(() => {
    const fromHash = typeof window !== 'undefined' ? tabFromHash(window.location.hash) : 'identity'
    if (!hiddenTabs.includes(fromHash)) return fromHash
    return TABS.find((tab) => !hiddenTabs.includes(tab.id))?.id ?? 'identity'
  })

  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [website, setWebsite] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [postalCode, setPostalCode] = useState<string>('')
  const [point, setPoint] = useState<MapPickerPoint | null>(null)
  const [accessType, setAccessType] = useState<string>('')
  const [accessFeeNotes, setAccessFeeNotes] = useState<string>('')
  const [servicesNotes, setServicesNotes] = useState<string>('')
  const [equipmentRentalNotes, setEquipmentRentalNotes] = useState<string>('')
  const [inforandoToken, setInforandoToken] = useState<string>('')
  const [lodgingUnits, setLodgingUnits] = useState<
    Array<{
      key: string
      building_type_id: number | ''
      name: string
      quantity: string
      capacity: string
    }>
  >([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { data: filterOptions } = useFilterOptions()

  useEffect(() => {
    if (organization) {
      setName(organization.name || '')
      setDescription(organization.description || '')
      setWebsite(organization.website || '')
      setEmail(organization.email || '')
      setPhone(organization.phone || '')
      setAddress(organization.address || '')
      setCity(organization.city || '')
      setPostalCode(organization.postal_code || '')
      setPoint(
        organization.coordinates &&
          Number.isFinite(organization.coordinates.lat) &&
          Number.isFinite(organization.coordinates.lng)
          ? {
              lat: organization.coordinates.lat,
              lng: organization.coordinates.lng,
            }
          : null,
      )
      setAccessType(organization.access_type || '')
      setAccessFeeNotes(organization.access_fee_notes || '')
      setServicesNotes(organization.services_notes || '')
      setEquipmentRentalNotes(organization.equipment_rental_notes || '')
      setInforandoToken(organization.inforando_token || '')
      setLodgingUnits(
        (organization.lodging_units ?? []).map((u, i) => ({
          key: String(u.id ?? `u-${i}`),
          building_type_id: u.building_type?.id ?? u.building_type_id ?? '',
          name: u.name ?? '',
          quantity: String(u.quantity ?? 1),
          capacity: u.capacity != null ? String(u.capacity) : '',
        })),
      )
    }
  }, [organization])

  useEffect(() => {
    const onHash = () => setActiveTab(tabFromHash(window.location.hash))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const selectTab = (tab: OrgSettingsTab) => {
    setActiveTab(tab)
    const meta = TABS.find((t) => t.id === tab)
    if (meta && typeof history !== 'undefined' && history.replaceState) {
      history.replaceState(null, '', `#${meta.hash}`)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)
    setSuccessMessage(null)

    if (!name.trim()) {
      setErrorMessage(tr('OrganizationSettings.nom_requis'))
      selectTab('identity')
      return
    }

    try {
      await updateMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        website: website.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        address: address.trim() || null,
        city: city.trim() || null,
        postal_code: postalCode.trim() || null,
        coordinates: point
          ? { lat: point.lat, lng: point.lng }
          : null,
        access_type: accessType || null,
        access_fee_notes: accessFeeNotes.trim() || null,
        services_notes: servicesNotes.trim() || null,
        equipment_rental_notes: equipmentRentalNotes.trim() || null,
        inforando_token: inforandoToken.trim() || null,
        lodging_units: lodgingUnits
          .filter((u) => u.building_type_id !== '')
          .map((u) => ({
            building_type_id: u.building_type_id as number,
            name: u.name.trim() || undefined,
            quantity: Math.max(1, parseInt(u.quantity, 10) || 1),
            capacity: u.capacity.trim() ? parseInt(u.capacity, 10) || null : null,
          })),
      })

      setSuccessMessage(tr('OrganizationSettings.profil_mis_jour_avec_succes'))
    } catch (err) {
      if (err instanceof HttpError) {
        setErrorMessage(
          err.message
            ? tr('OrganizationSettings.erreur', { message: err.message })
            : tr('OrganizationSettings.erreur_lors_mise_jour'),
        )
      } else if (err instanceof Error) {
        setErrorMessage(tr('OrganizationSettings.erreur', { message: err.message }))
      } else {
        setErrorMessage(tr('OrganizationSettings.erreur_lors_mise_jour'))
      }
      console.error('Error updating organization:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="organization-settings organization-settings--loading">
        <div className="organization-settings__skeleton" />
        <div className="organization-settings__skeleton" />
        <div className="organization-settings__skeleton" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="organization-settings organization-settings--error">
        <p className="organization-settings__error-message">
          {tr('OrganizationSettings.erreur_lors_chargement', { message: error.message })}
        </p>
      </div>
    )
  }

  if (!organization) {
    return (
      <div className="organization-settings organization-settings--error">
        <p className="organization-settings__error-message">{tr('OrganizationSettings.organisation_introuvable')}</p>
      </div>
    )
  }

  const showSave =
    activeTab === 'identity' || activeTab === 'access' || activeTab === 'location'

  return (
    <div className="organization-settings">
      {/* Un seul onglet (ex. la tarification seule dans le site de gestion) : ni en-tête ni barre d'onglets. */}
      {visibleTabs.length > 1 && (
      <header className="organization-settings__header">
        <div>
          <h2 className="organization-settings__title">{tr('OrganizationSettings.parametres_organisation')}</h2>
          <p className="organization-settings__subtitle">{organization.name}</p>
        </div>
      </header>
      )}

      <div
        hidden={visibleTabs.length <= 1}
        className="organization-settings__tabs"
        role="tablist"
        aria-label={tr('OrganizationSettings.sections_parametres')}
      >
        {visibleTabs.map((tab) => {
          const selected = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${tabsId}-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${tabsId}-panel-${tab.id}`}
              className={
                selected
                  ? 'organization-settings__tab organization-settings__tab--active'
                  : 'organization-settings__tab'
              }
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {(errorMessage || successMessage) && (
        <div className="organization-settings__flash">
          {errorMessage && (
            <div className="organization-settings__error" role="alert">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="organization-settings__success" role="alert">
              {successMessage}
            </div>
          )}
        </div>
      )}

      <div className="organization-settings__panels">
        <form
          className="organization-settings__form"
          onSubmit={handleSubmit}
          hidden={
            activeTab !== 'identity' &&
            activeTab !== 'access' &&
            activeTab !== 'location'
          }
        >
          <div
            role="tabpanel"
            id={`${tabsId}-panel-identity`}
            aria-labelledby={`${tabsId}-identity`}
            hidden={activeTab !== 'identity'}
            className="organization-settings__panel"
          >
            <div className="organization-settings__grid">
              <div className="organization-settings__field organization-settings__field--full">
                <label htmlFor="org-name" className="organization-settings__label">
                  {tr('OrganizationSettings.nom')} <span className="organization-settings__required">*</span>
                </label>
                <input
                  id="org-name"
                  type="text"
                  className="organization-settings__input"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    setErrorMessage(null)
                  }}
                  required
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="organization-settings__field organization-settings__field--full">
                <label htmlFor="org-description" className="organization-settings__label">
                  {tr('OrganizationSettings.description')}
                </label>
                <textarea
                  id="org-description"
                  className="organization-settings__textarea"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value)
                    setErrorMessage(null)
                  }}
                  rows={5}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="organization-settings__field">
                <label htmlFor="org-website" className="organization-settings__label">
                  {tr('OrganizationSettings.site_web')}
                </label>
                <input
                  id="org-website"
                  type="url"
                  className="organization-settings__input"
                  value={website}
                  onChange={(e) => {
                    setWebsite(e.target.value)
                    setErrorMessage(null)
                  }}
                  placeholder="https://example.com"
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="organization-settings__field">
                <label htmlFor="org-email" className="organization-settings__label">
                  {tr('OrganizationSettings.email')}
                </label>
                <input
                  id="org-email"
                  type="email"
                  className="organization-settings__input"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setErrorMessage(null)
                  }}
                  placeholder="contact@example.com"
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="organization-settings__field">
                <label htmlFor="org-phone" className="organization-settings__label">
                  {tr('OrganizationSettings.telephone')}
                </label>
                <input
                  id="org-phone"
                  type="tel"
                  className="organization-settings__input"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value)
                    setErrorMessage(null)
                  }}
                  placeholder="+1 418 …"
                  disabled={updateMutation.isPending}
                />
              </div>
            </div>
          </div>

          <div
            role="tabpanel"
            id={`${tabsId}-panel-location`}
            aria-labelledby={`${tabsId}-location`}
            hidden={activeTab !== 'location'}
            className="organization-settings__panel"
          >
            <p className="organization-settings__hint">
              {tr('OrganizationSettings.point_gps_doit_representer_accueil')}
            </p>
            <div className="organization-settings__geo-status" aria-live="polite">
              <span
                className={
                  point
                    ? 'organization-settings__geo-pill organization-settings__geo-pill--ok'
                    : 'organization-settings__geo-pill organization-settings__geo-pill--missing'
                }
              >
                {point ? tr('OrganizationSettings.accueil_point') : tr('OrganizationSettings.accueil_point_manquant')}
              </span>
              <span
                className={
                  organization.boundary
                    ? 'organization-settings__geo-pill organization-settings__geo-pill--ok'
                    : 'organization-settings__geo-pill organization-settings__geo-pill--missing'
                }
              >
                {organization.boundary
                  ? tr('OrganizationSettings.perimetre_zone')
                  : tr('OrganizationSettings.perimetre_absent')}
              </span>
            </div>
            <div className="organization-settings__grid">
              <div className="organization-settings__field organization-settings__field--full">
                {renderLocationMap ? (
                  renderLocationMap({ point, onPointChange: setPoint, disabled: updateMutation.isPending })
                ) : (
                  <MapPicker
                    value={point}
                    onChange={setPoint}
                    defaultCenter={
                      point
                        ? [point.lat, point.lng]
                        : organization.boundary_bbox
                          ? [
                              (organization.boundary_bbox.south +
                                organization.boundary_bbox.north) /
                                2,
                              (organization.boundary_bbox.west +
                                organization.boundary_bbox.east) /
                                2,
                            ]
                          : [46.3432, -72.5793]
                    }
                    defaultZoom={point ? 14 : 5}
                    fitBounds={organization.boundary_bbox ?? null}
                    boundaryGeometry={
                      organization.boundary?.type === 'Polygon' ||
                      organization.boundary?.type === 'MultiPolygon'
                        ? (organization.boundary as MapPickerBoundaryGeometry)
                        : null
                    }
                    height="min(420px, 55vh)"
                    disabled={updateMutation.isPending}
                    labelId="org-settings-map-label"
                    hint={tr('OrganizationSettings.cliquez_carte_placer_accueil_principal')}
                  />
                )}
              </div>
              <div className="organization-settings__field organization-settings__field--full">
                <label htmlFor="org-address" className="organization-settings__label">
                  {tr('OrganizationSettings.adresse')}
                </label>
                <input
                  id="org-address"
                  type="text"
                  className="organization-settings__input"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value)
                    setErrorMessage(null)
                  }}
                  disabled={updateMutation.isPending}
                />
              </div>
              <div className="organization-settings__field">
                <label htmlFor="org-city" className="organization-settings__label">
                  {tr('OrganizationSettings.ville')}
                </label>
                <input
                  id="org-city"
                  type="text"
                  className="organization-settings__input"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value)
                    setErrorMessage(null)
                  }}
                  disabled={updateMutation.isPending}
                />
              </div>
              <div className="organization-settings__field">
                <label htmlFor="org-postal" className="organization-settings__label">
                  {tr('OrganizationSettings.code_postal')}
                </label>
                <input
                  id="org-postal"
                  type="text"
                  className="organization-settings__input"
                  value={postalCode}
                  onChange={(e) => {
                    setPostalCode(e.target.value)
                    setErrorMessage(null)
                  }}
                  disabled={updateMutation.isPending}
                />
              </div>
            </div>
          </div>

          <div
            role="tabpanel"
            id={`${tabsId}-panel-access`}
            aria-labelledby={`${tabsId}-access`}
            hidden={activeTab !== 'access'}
            className="organization-settings__panel"
          >
            <div className="organization-settings__grid">
              <div className="organization-settings__field">
                <label htmlFor="org-access-type" className="organization-settings__label">
                  {tr('OrganizationSettings.type_acces')}
                </label>
                <select
                  id="org-access-type"
                  className="organization-settings__input"
                  value={accessType}
                  onChange={(e) => {
                    setAccessType(e.target.value)
                    setErrorMessage(null)
                  }}
                  disabled={updateMutation.isPending}
                >
                  <option value="">{tr('OrganizationSettings.non_renseigne')}</option>
                  <option value="public">{tr('OrganizationSettings.public')}</option>
                  <option value="clients_only">{tr('OrganizationSettings.clientele_seulement')}</option>
                  <option value="mixed">{tr('OrganizationSettings.mixte')}</option>
                </select>
              </div>

              <div className="organization-settings__field organization-settings__field--full">
                <label htmlFor="org-access-fee-notes" className="organization-settings__label">
                  {tr('OrganizationSettings.frais_acces_notes')}
                </label>
                <textarea
                  id="org-access-fee-notes"
                  className="organization-settings__textarea"
                  value={accessFeeNotes}
                  onChange={(e) => {
                    setAccessFeeNotes(e.target.value)
                    setErrorMessage(null)
                  }}
                  rows={3}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="organization-settings__field organization-settings__field--full">
                <label htmlFor="org-services-notes" className="organization-settings__label">
                  {tr('OrganizationSettings.services_notes')}
                </label>
                <textarea
                  id="org-services-notes"
                  className="organization-settings__textarea"
                  value={servicesNotes}
                  onChange={(e) => {
                    setServicesNotes(e.target.value)
                    setErrorMessage(null)
                  }}
                  rows={3}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="organization-settings__field organization-settings__field--full">
                <label htmlFor="org-equipment-notes" className="organization-settings__label">
                  {tr('OrganizationSettings.location_equipement_notes')}
                </label>
                <textarea
                  id="org-equipment-notes"
                  className="organization-settings__textarea"
                  value={equipmentRentalNotes}
                  onChange={(e) => {
                    setEquipmentRentalNotes(e.target.value)
                    setErrorMessage(null)
                  }}
                  rows={3}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="organization-settings__field organization-settings__field--full">
                <h3 className="organization-settings__section-heading">{tr('OrganizationSettings.inforando_sentiers')}</h3>
                <p className="organization-settings__hint">
                  {tr('OrganizationSettings.token_bearer_fourni_par_inforando')}
                </p>
                <label htmlFor="org-inforando-token" className="organization-settings__label">
                  {tr('OrganizationSettings.token_inforando')}
                </label>
                <input
                  id="org-inforando-token"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  className="organization-settings__input"
                  value={inforandoToken}
                  onChange={(e) => {
                    setInforandoToken(e.target.value)
                    setErrorMessage(null)
                  }}
                  placeholder={tr('OrganizationSettings.coller_token')}
                  disabled={updateMutation.isPending}
                />
              </div>

              {!hideLodgingUnits && (
              <div className="organization-settings__field organization-settings__field--full">
                <h3 className="organization-settings__section-heading">{tr('OrganizationSettings.inventaire_pret_camper')}</h3>
                <p className="organization-settings__hint">
                  {tr('OrganizationSettings.inventaire_global_sync_campingquebec_com')}
                </p>
                <div className="organization-settings__lodging-list">
                  {lodgingUnits.map((row, index) => (
                    <div key={row.key} className="organization-settings__lodging-row">
                      <div>
                        <label className="organization-settings__label">{tr('OrganizationSettings.type')}</label>
                        <select
                          className="organization-settings__input"
                          value={row.building_type_id}
                          onChange={(e) => {
                            const next = [...lodgingUnits]
                            next[index] = {
                              ...row,
                              building_type_id: e.target.value ? Number(e.target.value) : '',
                            }
                            setLodgingUnits(next)
                          }}
                          disabled={updateMutation.isPending}
                        >
                          <option value="">—</option>
                          {(filterOptions?.refuges ?? []).map((bt) => (
                            <option key={bt.id} value={bt.id}>
                              {bt.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="organization-settings__label">{tr('OrganizationSettings.qte')}</label>
                        <input
                          type="number"
                          min={1}
                          className="organization-settings__input"
                          value={row.quantity}
                          onChange={(e) => {
                            const next = [...lodgingUnits]
                            next[index] = { ...row, quantity: e.target.value }
                            setLodgingUnits(next)
                          }}
                          disabled={updateMutation.isPending}
                        />
                      </div>
                      <div>
                        <label className="organization-settings__label">{tr('OrganizationSettings.cap')}</label>
                        <input
                          type="number"
                          min={0}
                          className="organization-settings__input"
                          value={row.capacity}
                          onChange={(e) => {
                            const next = [...lodgingUnits]
                            next[index] = { ...row, capacity: e.target.value }
                            setLodgingUnits(next)
                          }}
                          disabled={updateMutation.isPending}
                        />
                      </div>
                      <button
                        type="button"
                        className="organization-settings__button organization-settings__button--secondary"
                        onClick={() =>
                          setLodgingUnits(lodgingUnits.filter((_, i) => i !== index))
                        }
                        disabled={updateMutation.isPending}
                      >
                        {tr('OrganizationSettings.retirer')}
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="organization-settings__button organization-settings__button--secondary"
                    onClick={() =>
                      setLodgingUnits([
                        ...lodgingUnits,
                        {
                          key: `new-${Date.now()}`,
                          building_type_id: '',
                          name: '',
                          quantity: '1',
                          capacity: '',
                        },
                      ])
                    }
                    disabled={updateMutation.isPending}
                  >
                    {tr('OrganizationSettings.ajouter_unite')}
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>

          {showSave && (
            <div className="organization-settings__actions">
              <button
                type="submit"
                className="organization-settings__button"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? tr('OrganizationSettings.enregistrement') : tr('OrganizationSettings.enregistrer')}
              </button>
            </div>
          )}
        </form>
        {activeTab === 'location' && locationExtra}

        <div
          role="tabpanel"
          id={`${tabsId}-panel-facilities`}
          aria-labelledby={`${tabsId}-facilities`}
          hidden={activeTab !== 'facilities'}
          className="organization-settings__panel organization-settings__panel--standalone"
        >
          <OrganizationSiteAssetsEditor organizationId={organizationId} />
        </div>

        <div
          role="tabpanel"
          id={`${tabsId}-panel-pricing`}
          aria-labelledby={`${tabsId}-pricing`}
          hidden={activeTab !== 'pricing'}
          className="organization-settings__panel organization-settings__panel--standalone"
        >
          <OrganizationPricingProfilesSection
            organizationId={organizationId}
            disabled={updateMutation.isPending}
          />
        </div>

        <div
          role="tabpanel"
          id={`${tabsId}-panel-media`}
          aria-labelledby={`${tabsId}-media`}
          hidden={activeTab !== 'media'}
          className="organization-settings__panel organization-settings__panel--standalone"
        >
          <PrimaryGalleryEditor
            organizationId={organizationId}
            primaryGallery={organization.primary_gallery}
            disabled={updateMutation.isPending}
          />
          {/* Cartes et plans du site : ils décrivent l'organisation (pas un équipement), d'où les Médias. */}
          <hr className="organization-settings__sep" />
          <OrganizationSitePlansEditor organizationId={organizationId} />
        </div>
      </div>
    </div>
  )
}
